import React, { useContext, useMemo, ReactNode } from 'react';
import { useMAppAuth } from '../hooks/useMAppAuth';
import {
  resolveCapabilities,
  hasCapability,
  hasAnyCapability,
  hasAllCapabilities,
  type Capability,
  type Role,
} from '@mapp-byte-connect/config';

interface IUser {
  yumId: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  permissions?: string[];
}

interface IUserContextValue {
  isLoading: boolean;
  logout: () => void;
  initAuth: () => void;
  retryAuth: () => void;
  user?: IUser;
  authError: string | null;
  brandId?: string | null;
  environment?: string;
  /** Resolved roles from the permissions config */
  userRoles: Role[];
  /** Resolved capabilities from the permissions config */
  userCapabilities: Set<Capability>;
}

export const UserContext = React.createContext<IUserContextValue>({
  isLoading: false,
  logout: () => {},
  initAuth: () => {},
  retryAuth: () => {},
  user: undefined,
  authError: null,
  userRoles: [],
  userCapabilities: new Set(),
});

interface IUserContextProviderProps {
  children: ReactNode;
}

export function UserContextProvider({ children }: IUserContextProviderProps): JSX.Element {
  const { isLoading, user, authError, logout, initAuth, retryAuth, brandId, environment } =
    useMAppAuth();

  const { userRoles, userCapabilities } = useMemo(() => {
    if (!user?.roles) {
      return { userRoles: [] as Role[], userCapabilities: new Set<Capability>() };
    }
    const resolved = resolveCapabilities(user.roles);
    return { userRoles: resolved.roles, userCapabilities: resolved.capabilities };
  }, [user?.roles]);

  return (
    <UserContext.Provider
      value={{
        isLoading,
        user,
        authError,
        logout,
        initAuth,
        retryAuth,
        brandId,
        environment,
        userRoles,
        userCapabilities,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export const useUserContext = () => useContext(UserContext);

/**
 * Hook for checking user capabilities in components.
 *
 * Usage:
 *   const { can, canAny, canAll } = usePermissions();
 *   if (can(Capability.FORM_CREATE)) { ... }
 */
export function usePermissions() {
  const { userCapabilities, userRoles } = useUserContext();

  return {
    /** Check if user has a specific capability */
    can: (capability: Capability) => hasCapability(userCapabilities, capability),
    /** Check if user has ANY of the listed capabilities */
    canAny: (capabilities: Capability[]) => hasAnyCapability(userCapabilities, capabilities),
    /** Check if user has ALL of the listed capabilities */
    canAll: (capabilities: Capability[]) => hasAllCapabilities(userCapabilities, capabilities),
    /** The user's resolved roles */
    roles: userRoles,
    /** The user's resolved capabilities set */
    capabilities: userCapabilities,
  };
}
